import Axios from '@/utils/axios';
import { ApiKeyEnvironment } from '@/services/api/apiKeys';

// ─── Developer webhook endpoints ──────────────────────────────────────
// Self-service webhook management on the logistic integration surface:
//   POST /v1/integration/webhook-endpoints             register (secret returned ONCE)
//   GET  /v1/integration/webhook-endpoints             list own endpoints (no secret)
//   POST /v1/integration/webhook-endpoints/:id/disable stop deliveries (irreversible)
// JWT-authed (an API key cannot point deliveries somewhere new); the caller's
// client scope comes from the bearer token. Same environment wall as keys:
// TEST endpoints receive only sandbox events, LIVE only production.
// Backend design: nest-logistic-service docs/api-integration/webhook.md;
// payload contract: docs/api-integration/webhook-payload-v2.md.
const CHANNEL_HEADERS_LIVE = {
  'X-Channel': 'LINK',
  'X-Api-Mode': 'live',
} as const;

/** Every endpoint receives ALL shipment events — no subscription list. One
 *  row per change Dash sends: a status change goes out as `STATUS_CHANGED`
 *  with the new `event.status`, a 4W milestone as `MILESTONE_COMPLETED`
 *  (`PICKED_UP` is reserved, not emitted). Keep in sync with
 *  nest-logistic-service docs/api-integration/webhook-payload-v2.md §4, §8. */
export const SHIPMENT_WEBHOOK_EVENTS = [
  {
    type: 'STATUS_CHANGED',
    status: 'CREATED',
    message: 'Shipment terbuat',
    when: 'Shipment created — via the API, a CSV import, or by Dash ops',
  },
  {
    type: 'STATUS_CHANGED',
    status: 'AT_HUB',
    // {hubName}: the hub's name when known; dropped (with its space) when not.
    message:
      'Shipment sudah tiba di gudang {hubName} dan sudah di-scan oleh PIC gudang',
    when: 'Scanned in at a Dash hub, including a re-scan after a failed attempt',
  },
  {
    type: 'STATUS_CHANGED',
    status: 'PARTIALLY_DISPATCHED',
    message: 'Sebagian paket sudah di-dispatch dan di-assign ke rider',
    when: 'Some items scheduled to a courier, the rest still at the hub',
  },
  {
    type: 'STATUS_CHANGED',
    status: 'DISPATCHED',
    // {riderName}: the rider just assigned; dropped when not known.
    message: 'Shipment sudah di-dispatch dan di-assign ke rider {riderName}',
    when: 'Every active item scheduled to a courier',
  },
  {
    type: 'STATUS_CHANGED',
    status: 'IN_DELIVERY',
    message: 'Dalam pengantaran ke lokasi tujuan',
    when: 'Courier on the way — again after a failed attempt is retried',
  },
  {
    type: 'STATUS_CHANGED',
    status: 'FAILED',
    message: 'The standard reason text (fallback: Pengantaran gagal)',
    when: 'A delivery attempt failed, or Dash closed the shipment as failed; sent again if the reason changes',
  },
  {
    type: 'STATUS_CHANGED',
    status: 'IN_DISPUTE',
    message: 'Paket ditolak penerima dan dalam proses retur',
    when: 'An item was rejected and is on its way back',
  },
  {
    type: 'STATUS_CHANGED',
    status: 'COMPLETED',
    message: 'Pengantaran selesai',
    when: 'Every active item resolved, at least one delivered',
  },
  {
    type: 'STATUS_CHANGED',
    status: 'RETURNED',
    message: 'Paket sudah dikembalikan',
    when: 'Every active item returned, none delivered',
  },
  {
    type: 'STATUS_CHANGED',
    status: 'CANCELLED',
    message: 'Shipment dibatalkan',
    when: 'Every item cancelled',
  },
  {
    type: 'MILESTONE_COMPLETED',
    status: '(unchanged)',
    message: 'The milestone name',
    when: 'DIRECT_4W: a stop milestone was completed',
  },
] as const;

export interface WebhookEndpointRow {
  id: string;
  url: string;
  environment: ApiKeyEnvironment;
  disabledAt: string | null;
  createdBy: string;
  createdAt: string;
}

export interface CreateWebhookEndpointRequest {
  url: string;
  environment: ApiKeyEnvironment;
}

export interface CreateWebhookEndpointResponse {
  id: string;
  url: string;
  environment: ApiKeyEnvironment;
  createdAt: string;
  /** The `dash_whsec_…` signing secret — shown once, never retrievable again. */
  secret: string;
}

interface Envelope<T> {
  status: string;
  message?: string;
  data: T;
}

const BASE = '/v1/integration/webhook-endpoints';

export const listWebhookEndpoints = async (): Promise<WebhookEndpointRow[]> => {
  const res = await Axios.get<Envelope<{ data: WebhookEndpointRow[] }>>(BASE, {
    headers: CHANNEL_HEADERS_LIVE,
  });
  return res.data.data.data;
};

export const createWebhookEndpoint = async (
  body: CreateWebhookEndpointRequest
): Promise<CreateWebhookEndpointResponse> => {
  const res = await Axios.post<Envelope<CreateWebhookEndpointResponse>>(
    BASE,
    body,
    { headers: CHANNEL_HEADERS_LIVE }
  );
  return res.data.data;
};

export const disableWebhookEndpoint = async (id: string): Promise<void> => {
  await Axios.post(`${BASE}/${id}/disable`, undefined, {
    headers: CHANNEL_HEADERS_LIVE,
  });
};
